import { StyleSheet, View } from 'react-native';
import { BarChart3 } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type ExamMetric = {
  label: string;
  value: string;
  helper: string;
};

type ExamReportSummaryProps = {
  score: string;
  metrics: ExamMetric[];
  abilities: ExamMetric[];
};

export function ExamReportSummary({ score, metrics, abilities }: ExamReportSummaryProps) {
  return (
    <AppCard style={styles.card}>
      <View style={styles.header}>
        <BarChart3 size={20} color={tokens.colors.primary} />
        <AppText variant="body" weight="bold">
          成绩分析
        </AppText>
      </View>
      <View style={styles.scoreRow}>
        <AppText variant="metric" tone="primary" weight="bold">
          {score}
        </AppText>
        <AppText variant="caption" tone="muted">
          本次估算得分
        </AppText>
      </View>
      <View style={styles.metricsGrid}>
        {metrics.map((metric) => (
          <View key={metric.label} style={styles.metricItem}>
            <AppText variant="subtitle" weight="bold">
              {metric.value}
            </AppText>
            <AppText variant="caption" weight="bold">
              {metric.label}
            </AppText>
            <AppText variant="caption" tone="muted">
              {metric.helper}
            </AppText>
          </View>
        ))}
      </View>
      <View style={styles.section}>
        <AppText variant="caption" tone="primary" weight="bold">
          能力维度
        </AppText>
        {abilities.map((ability) => {
          const abilityWidth = ability.value as `${number}%`;

          return (
            <View key={ability.label} style={styles.abilityRow}>
              <AppText variant="caption" weight="bold" style={styles.abilityLabel}>
                {ability.label}
              </AppText>
              <View style={styles.track}>
                <View style={[styles.fill, { width: abilityWidth }]} />
              </View>
              <AppText variant="caption" tone="muted">
                {ability.value}
              </AppText>
            </View>
          );
        })}
      </View>
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  scoreRow: {
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  metricsGrid: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  metricItem: {
    flex: 1,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.sm,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  section: {
    gap: tokens.spacing.sm,
  },
  abilityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  abilityLabel: {
    width: 64,
  },
  track: {
    flex: 1,
    height: tokens.layout.progressHeight,
    borderRadius: tokens.radius.full,
    overflow: 'hidden',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  fill: {
    height: '100%',
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.colors.primary,
  },
});
